import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import {
  TGetLoseReason as TLoseReason,
  TGetLoseReasonParams as TParams
} from '@/typings/api/twitter_instantwin_message_history.type'

export type TwitterInstantwinLoseReasonState = {
  error: boolean
  loseReasons: TLoseReason[]
  loading: boolean
  params: TParams
}

const createInitialState = (): TwitterInstantwinLoseReasonState => ({
  error: false,
  loseReasons: [],
  loading: false,
  params: {
    campaignId: 0,
    sortBy: '-count'
  }
})

export const useTwitterInstantwinLoseReasonStore = defineStore('twitterInstantwinLoseReasonStore', () => {
  const initialState = createInitialState()

  const error = ref<TwitterInstantwinLoseReasonState['error']>(initialState.error)
  const loseReasons = ref<TwitterInstantwinLoseReasonState['loseReasons']>(initialState.loseReasons)
  const loading = ref<TwitterInstantwinLoseReasonState['loading']>(initialState.loading)
  const params = ref<TwitterInstantwinLoseReasonState['params']>(initialState.params)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    error.value = nextState.error
    loseReasons.value = nextState.loseReasons
    loading.value = nextState.loading
    params.value = nextState.params
  }

  const fetchLoseReasons = async (): Promise<void> => {
    if (params.value.campaignId === 0) {
      return
    }

    error.value = false
    loading.value = true

    const result = await api.get('/twitter_instantwin_message_histories/lose_reasons', { params: params.value })

    loading.value = false

    if (!result.data) {
      error.value = true
      return
    }

    loseReasons.value = result.data.loseReasons
  }

  const setParams = (newParams: Partial<TParams>): void => {
    params.value = {
      ...params.value,
      ...newParams
    }
  }

  return {
    error: readonly(error),
    loseReasons: readonly(loseReasons),
    loading: readonly(loading),
    params: readonly(params),
    reset,
    fetchLoseReasons,
    setParams
  }
})
